import React, { useState } from 'react';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { Reveal } from './Section';
import { CREATIVE_TYPES } from '../data/mock';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignUpForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    type: '',
    city: '',
    emailConsent: true,
    smsConsent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.type) {
      setError('Name, email, and creative type are required.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.smsConsent && !form.phone) {
      setError('Please enter your mobile number to receive text updates, or uncheck SMS consent.');
      return;
    }
    if (form.smsConsent && form.phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit mobile number for SMS updates.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/signup`, form, { timeout: 20000 });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again in a moment.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(900px 500px at 50% 30%, rgba(34,167,115,0.16), transparent 60%), linear-gradient(180deg, #000 0%, #03110a 100%)',
          }}
        />
        <div className="absolute inset-0 hero-grain" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal delay={80}>
            <h2 className="font-display text-white text-[40px] md:text-[56px] lg:text-[68px] leading-[0.98] tracking-[-0.04em]">
              Join The First<br />
              <span className="text-[#CFA153]">200.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-white/65 text-[15.5px] leading-[1.7] mt-6 max-w-[440px]">
              Drop your info to lock in founding-member access to SAMZ Academy and get launch news, drop dates, and early-access invites straight to your inbox.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <ul className="mt-8 space-y-3">
              {[
                'Founding-member pricing on the 6-course certification',
                'Priority access to the AI-powered gig marketplace',
                'Launch alerts and drop reminders by email',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/75 text-[14px]">
                  <span className="w-5 h-5 rounded-full bg-[#22A773]/15 flex items-center justify-center mt-0.5 shrink-0">
                    <Check size={12} className="text-[#22A773]" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-6">
          <Reveal delay={120}>
            <div className="card-border rounded-2xl p-8 lg:p-10 bg-black/40 backdrop-blur">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-[#22A773]/15 mx-auto flex items-center justify-center mb-6">
                    <Check size={28} className="text-[#22A773]" />
                  </div>
                  <h3 className="font-display text-white text-[28px] md:text-[34px] leading-[1.05] tracking-[-0.02em] mb-3">
                    You're In. Welcome to SAMZ.
                  </h3>
                  <p className="text-white/65 text-[14.5px] leading-[1.7] max-w-[440px] mx-auto">
                    Check your inbox for next steps. We'll be in touch with founding-cohort details, launch news, and your early-access link soon.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name:'', email:'', phone:'', type:'', city:'', emailConsent: true, smsConsent: false }); }}
                    className="mt-8 text-[#22A773] text-[12.5px] tracking-[0.14em] uppercase hover:text-[#CFA153]"
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <div className="pin-num mb-2 !text-[10px]">Full Name *</div>
                    <input
                      type="text"
                      value={form.name}
                      onChange={onChange('name')}
                      placeholder="Your name"
                      className="w-full bg-black/60 border border-white/12 rounded-lg px-4 py-3 text-white text-[14.5px] placeholder:text-white/30 focus:border-[#22A773] focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <div className="pin-num mb-2 !text-[10px]">Email *</div>
                      <input
                        type="email"
                        value={form.email}
                        onChange={onChange('email')}
                        placeholder="you@email.com"
                        className="w-full bg-black/60 border border-white/12 rounded-lg px-4 py-3 text-white text-[14.5px] placeholder:text-white/30 focus:border-[#22A773] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <div className="pin-num mb-2 !text-[10px]">Mobile Phone</div>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={onChange('phone')}
                        placeholder="(555) 555-5555"
                        className="w-full bg-black/60 border border-white/12 rounded-lg px-4 py-3 text-white text-[14.5px] placeholder:text-white/30 focus:border-[#22A773] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <div className="pin-num mb-2 !text-[10px]">I am a... *</div>
                      <select
                        value={form.type}
                        onChange={onChange('type')}
                        className="w-full bg-black/60 border border-white/12 rounded-lg px-4 py-3 text-white text-[14.5px] focus:border-[#22A773] focus:outline-none transition-colors appearance-none"
                      >
                        <option value="" className="bg-black">Select your craft</option>
                        {CREATIVE_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-black">{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="pin-num mb-2 !text-[10px]">City</div>
                      <input
                        type="text"
                        value={form.city}
                        onChange={onChange('city')}
                        placeholder="Los Angeles, CA"
                        className="w-full bg-black/60 border border-white/12 rounded-lg px-4 py-3 text-white text-[14.5px] placeholder:text-white/30 focus:border-[#22A773] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-white/8 space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.emailConsent}
                        onChange={onChange('emailConsent')}
                        className="mt-1 w-4 h-4 accent-[#22A773] shrink-0"
                      />
                      <span className="text-white/70 text-[12.5px] leading-[1.55]">
                        <span className="text-white">Email updates.</span> I agree to receive launch news, course drops, and founding-member offers from SAMZ Academy at the email above. Unsubscribe anytime.
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="text-[#ff6b6b] text-[12.5px]">{error}</div>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <div className="text-white/40 text-[11.5px] font-mono tracking-[0.16em] uppercase">
                      Step 01 · Founding Cohort
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-jade whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Submitting
                        </>
                      ) : (
                        <>Sign Up <ArrowUpRight size={16} /></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
